// Light/dark switch for the page chrome. Sits beside the notification bell
// and deliberately mirrors its trigger styling so the two read as a pair.
//
// The theme itself is CSS: index.css declares the palette under `:root` and an
// inverted set under `.dark`, and tailwind.config runs darkMode: ["class"].
// All this component does is decide whether that class is on <html>.
//
// Order of precedence for the initial value:
//   1. what the user last chose here (localStorage)
//   2. the OS preference, if they have never chosen
//   3. light
//
// The pre-paint half lives in public/theme-boot.js — a class applied on mount
// would flash light before React runs. That file and STORAGE_KEY here
// must agree; the key is repeated there because that file cannot import. It is
// a separate file rather than an inline block because the app ships an
// enforced CSP with script-src 'self' and no 'unsafe-inline'.
import { useCallback, useEffect, useState } from 'react';
import { Moon, Sun } from 'lucide-react';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import { TOOLTIP_DELAY_MS } from './chromeControls';

export const STORAGE_KEY = 'nombase.theme';

/** Read the theme without touching the DOM. Storage can throw in private
 *  mode and matchMedia is absent in jsdom, so both are guarded. */
export function resolveInitialTheme() {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved === 'dark' || saved === 'light') return saved;
  } catch {
    /* storage unavailable — fall through to the OS preference */
  }
  try {
    if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) return 'dark';
  } catch {
    /* matchMedia unavailable (jsdom) — fall through */
  }
  return 'light';
}

export default function ThemeToggle() {
  const [theme, setTheme] = useState(resolveInitialTheme);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    try {
      window.localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      /* the choice just will not survive a reload; not worth failing over */
    }
  }, [theme]);

  const toggle = useCallback(
    () => setTheme((t) => (t === 'dark' ? 'light' : 'dark')),
    [],
  );

  const goingTo = theme === 'dark' ? 'light' : 'dark';

  // Same delay as the bell and the guide beside it. This used the Radix
  // default (~700ms), which on a quick hover reads as having no label at all —
  // three adjacent controls should not disagree about that.
  return (
    <TooltipProvider delayDuration={TOOLTIP_DELAY_MS}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={toggle}
            aria-label={`Switch to ${goingTo} mode`}
            // aria-pressed rather than a role=switch: this is a button whose
            // effect is global, and screen readers announce the state change
            // via the label above.
            aria-pressed={theme === 'dark'}
            data-testid="theme-toggle"
            className="relative flex items-center justify-center w-7 h-7 rounded-full bg-card shadow-sm hover:bg-muted/60 transition-colors"
          >
            {theme === 'dark'
              ? <Sun className="w-3.5 h-3.5 text-gray-700" aria-hidden="true" />
              : <Moon className="w-3.5 h-3.5 text-gray-700" aria-hidden="true" />}
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom">{`Switch to ${goingTo} mode`}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
